import { HttpAxios } from "@/common/services/httpAxios";
import {
  IInsights,
  ICard,
  IResourceContent,
  IDashboardCampaignResults,
  IDashboardEngagement,
  IHasIntegration,
  IProductsInNewPricing,
} from "./interfaceInsights";
import { IAccount } from "@/common/services/cas/interfacesCas";

//dxpAccountId conta WakeStore
const account: IAccount = JSON.parse(localStorage.getItem("ACCOUNT") || "{}");
const activeMock =
  account?.account === process.env.VUE_APP_ACCOUNT_ID_WAKE_STORE;

const axiosSelected = {
  totalCampaignResults: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/dashboard/total-campaign-results.json"
      : `/Indicators/general-performance`,
    method: activeMock ? "get" : "post",
  },
  userHasGA: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/dashboard/user-has-ga.json"
      : `/PergamoIntegration/Integrations`,
    method: activeMock ? "get" : "get",
  },
  totalSubmissionsClicks: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/insights/total-submissions-clicks.json"
      : `/Indicators/total-submissions-clicks`,
    method: activeMock ? "get" : "post",
  },
  performanceInsights: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/insights/performance-insights.json"
      : `/Indicators/performance-insights`,
    method: activeMock ? "get" : "post",
  },
  allSalesResult: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/insights/all-sales-result.json"
      : `/Indicators/all-sales-result`,
    method: activeMock ? "get" : "post",
  },
  profilesQuantity: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/insights/profiles-quantity.json"
      : `/Indicators/profiles-quantity`,
    method: activeMock ? "get" : "post",
  },
  productsInNewPricing: {
    bffName: activeMock ? "mock" : "bffInsights",
    path: activeMock
      ? "/selected-customer/insights/products-in-new-pricing.json"
      : `/Indicators/performances/products`,
    method: "get",
  },
};

/**
 * @description service para o módulo de insights
 * @see http://beta-api-insights-bff.socialminer.tech/swagger/index.html - Beta com vpn ativa
 * @see http://api-insights-bff.socialminer.tech/swagger/index.html - Prod com vpn ativa
 */
class Insights {
  async getInsights(): Promise<IInsights | any> {
    try {
      const response = await HttpAxios.bffInsights.get(
        `/BeMax/alerts-insights`
      );

      return response.data.response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getDashboardCampaignResults(
    startDate: string,
    endDate: string
  ): Promise<IDashboardCampaignResults> {
    try {
      const response = await HttpAxios[
        axiosSelected.totalCampaignResults.bffName
      ][axiosSelected.totalCampaignResults.method](
        axiosSelected.totalCampaignResults.path,
        {
          startDate,
          endDate,
        }
      );
      return response.data.campaignResults;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUserHasGA(): Promise<IHasIntegration> {
    try {
      const response = await HttpAxios[axiosSelected.userHasGA.bffName][
        axiosSelected.userHasGA.method
      ](axiosSelected.userHasGA.path, { timeout: 60000 });

      return response.data.response[0];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getDashboardEngagement(
    startDate: string,
    endDate: string
  ): Promise<IDashboardEngagement> {
    try {
      const response = await HttpAxios[
        axiosSelected.totalSubmissionsClicks.bffName
      ][axiosSelected.totalSubmissionsClicks.method](
        axiosSelected.totalSubmissionsClicks.path,
        {
          startDate,
          endDate,
        }
      );
      return response.data.response.submissionsClicks;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getDashboardEngagementCards(
    startDate: Date,
    endDate: Date
  ): Promise<ICard[]> {
    try {
      const response = await HttpAxios[
        axiosSelected.performanceInsights.bffName
      ][axiosSelected.performanceInsights.method](
        axiosSelected.performanceInsights.path,
        { startDate, endDate }
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getDashboardArticles(
    startDate: Date,
    endDate: Date
  ): Promise<IResourceContent[][]> {
    const response: IResourceContent[][] = [];
    const getResourceRevenue = (result: PromiseSettledResult<any>) => {
      if (result.status === "fulfilled") {
        response.push(
          result.value.data.response.health
            ? result.value.data.response.health.insight.resources
            : []
        );
        return;
      }
      response.push([]);
      console.log("getDashboardArticles.\n", result.reason);
    };
    const getResourceBase = (result: PromiseSettledResult<any>) => {
      if (result.status === "fulfilled") {
        response.push(
          result.value.data.response.insight
            ? result.value.data.response.insight.resources
            : []
        );
        return;
      }
      response.push([]);
      console.log("getDashboardArticles.\n", result.reason);
    };
    const runCallback = [getResourceRevenue, getResourceBase];

    await Promise.allSettled([
      HttpAxios[axiosSelected.allSalesResult.bffName][
        axiosSelected.allSalesResult.method
      ](axiosSelected.allSalesResult.path, {
        startDate,
        endDate,
        recurring: "2",
      }),
      HttpAxios[axiosSelected.profilesQuantity.bffName][
        axiosSelected.profilesQuantity.method
      ](axiosSelected.profilesQuantity.path, [
        "EmailProfile",
        "PushProfile",
        "PhoneProfile",
      ]),
    ]).then((results) => {
      results.forEach((result, i) => {
        runCallback[i](result);
      });
    });

    return response;
  }

  async getProductsInNewPricing(): Promise<IProductsInNewPricing> {
    const response = await HttpAxios[
      axiosSelected.productsInNewPricing.bffName
    ][axiosSelected.productsInNewPricing.method](
      axiosSelected.productsInNewPricing.path
    );
    return response.data;
  }
}

export default new Insights();
